import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronRight, FileText, Download, Eye, Layers } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { API } from '../context/AuthContext';

const TYPE_LABELS = {
  notes: 'ନୋଟ୍ସ', lesson_plan: 'ପାଠ ଯୋଜନା', worksheet: 'ୱାର୍କସିଟ୍',
  question_bank: 'ପ୍ରଶ୍ନ ପତ୍ର', assessment: 'ମୂଲ୍ୟାଙ୍କନ', book: 'ପୁସ୍ତକ', activity: 'କାର୍ଯ୍ୟକଳାପ', pdf: 'PDF', image: 'ଛବି', video: 'ଭିଡିଓ', other: 'ଅନ୍ୟ'
};

export default function ResourceViewerPage() {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/discover/resource/${resourceId}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [resourceId]);

  if (loading) return <div className="p-6 text-center text-sm text-gray-500">Loading...</div>;
  if (!data) return (
    <div className="p-6 text-center">
      <div className="text-3xl">😔</div>
      <div className="text-sm font-bold text-gray-700 mt-2">Resource not found</div>
      <Link to="/teacher/browse" className="mt-3 inline-block text-xs text-emerald-600 underline">Browse resources</Link>
    </div>
  );

  const { resource: r, prev, next, related, breadcrumb, position } = data;
  const c = breadcrumb.class, s = breadcrumb.subject, t = breadcrumb.topic;
  const downloadUrl = `${API}/discover/resource/${r.id}/download`;

  return (
    <div className="px-4 pt-4 space-y-4 pb-8" data-testid="resource-viewer">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-[11px] text-gray-500 flex-wrap" data-testid="viewer-breadcrumb">
        <Link to="/" className="hover:text-emerald-600">Home</Link><ChevronRight size={11}/>
        <Link to="/teacher/browse" className="hover:text-emerald-600">Teacher</Link>
        {c && <><ChevronRight size={11}/><Link to={`/teacher/browse/${c.slug}`} className="hover:text-emerald-600">{c.name_odia}</Link></>}
        {s && <><ChevronRight size={11}/><Link to={`/teacher/browse/${c.slug}/${s.slug}`} className="hover:text-emerald-600">{s.name_odia}</Link></>}
        {t && <><ChevronRight size={11}/><Link to={`/teacher/browse/${c.slug}/${s.slug}/${t.slug}`} className="hover:text-emerald-600">{t.name_odia}</Link></>}
        <ChevronRight size={11}/>
        <span className="text-gray-800 font-semibold">{TYPE_LABELS[r.resource_type] || r.resource_type}</span>
      </nav>

      {/* Header */}
      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #059669 0%, #0891b2 100%)' }}>
        <div className="text-[10px] font-bold text-white/80 uppercase">{TYPE_LABELS[r.resource_type] || r.resource_type}</div>
        <h1 className="text-xl font-extrabold mt-1">{r.title_odia || r.title}</h1>
        {r.title_odia && r.title && <p className="text-xs text-white/85">{r.title}</p>}
        {position && <p className="text-[10px] text-white/70 mt-1">{position}</p>}
      </div>

      {/* Description */}
      {(r.description_odia || r.description) && (
        <Card className="rounded-2xl p-4 border-gray-200">
          {r.description_odia && <p className="text-sm text-gray-800 leading-relaxed">{r.description_odia}</p>}
          {r.description && <p className="text-xs text-gray-600 mt-2">{r.description}</p>}
        </Card>
      )}

      {/* Content */}
      {r.content && (
        <Card className="rounded-2xl p-4 border-gray-200">
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-800 text-sm leading-relaxed">{r.content}</div>
        </Card>
      )}

      {/* File / Download */}
      {(r.storage_path || r.file_url || r.original_filename) && (
        <Card className="rounded-2xl p-4 border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-3">
            <FileText size={24} className="text-emerald-600 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm text-emerald-900 truncate">{r.original_filename || 'Download File'}</div>
              {r.file_size && <div className="text-[11px] text-emerald-700">{(r.file_size/1024/1024).toFixed(2)} MB · {r.downloads || 0} downloads</div>}
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <a href={downloadUrl} target="_blank" rel="noreferrer" data-testid="download-resource-btn"
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700">
              <Download size={14}/> ଡାଉନଲୋଡ୍
            </a>
            <a href={downloadUrl} target="_blank" rel="noreferrer" data-testid="preview-resource-btn"
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white border-2 border-emerald-300 text-emerald-700 font-bold text-sm hover:bg-emerald-100">
              <Eye size={14}/> Preview
            </a>
          </div>
        </Card>
      )}

      {/* Prev/Next within same resource type */}
      <div className="flex gap-2">
        {prev ? (
          <button onClick={() => navigate(`/teacher/resource/${prev.id}`)} data-testid="prev-resource"
            className="flex-1 p-2 rounded-xl bg-white border-2 border-gray-200 hover:border-emerald-400 text-left flex items-center gap-2">
            <ArrowLeft size={14} className="shrink-0"/>
            <div className="min-w-0"><div className="text-[9px] text-gray-500">ପୂର୍ବବର୍ତ୍ତୀ</div><div className="text-[11px] font-bold truncate">{prev.title_odia || prev.title}</div></div>
          </button>
        ) : <div className="flex-1"></div>}
        {next ? (
          <button onClick={() => navigate(`/teacher/resource/${next.id}`)} data-testid="next-resource"
            className="flex-1 p-2 rounded-xl bg-white border-2 border-gray-200 hover:border-emerald-400 text-right flex items-center gap-2 justify-end">
            <div className="min-w-0"><div className="text-[9px] text-gray-500">ପରବର୍ତ୍ତୀ</div><div className="text-[11px] font-bold truncate">{next.title_odia || next.title}</div></div>
            <ChevronRight size={14} className="shrink-0"/>
          </button>
        ) : <div className="flex-1"></div>}
      </div>

      {/* Related */}
      {related.length > 0 && (
        <Card className="rounded-2xl p-4 border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Layers size={14} className="text-purple-600"/>
            <div className="font-bold text-sm">📚 Related Resources</div>
          </div>
          <div className="space-y-1.5">
            {related.map(x => (
              <Link key={x.id} to={`/teacher/resource/${x.id}`} data-testid={`related-${x.id}`}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-purple-50 border border-gray-200 hover:border-purple-300">
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-bold shrink-0">{TYPE_LABELS[x.resource_type] || x.resource_type}</span>
                <div className="flex-1 min-w-0"><div className="text-xs font-semibold truncate">{x.title_odia || x.title}</div></div>
                <ChevronRight size={12} className="text-gray-400"/>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
